export const getHighestDiscountPercentage = (product) => {
  return (
    product?.variants?.reduce((highest, variant) => {
      const discount = Math.round(
        ((variant.compareAtPrice?.amount - variant.price?.amount) * 100) /
          variant.compareAtPrice?.amount
      );
      return discount && discount > highest ? discount : highest;
    }, 0) || 0
  );
};

export const isNew = (product) => {
  const launchDateISO = product?.launchDate?.value;

  if (!launchDateISO) {
    return false;
  }

  const now = new Date();
  const launch = new Date(launchDateISO);
  const thirtyDaysAgo = new Date(new Date().setDate(now.getDate() - 30));

  // Product is considered new if it was launched within the last 30 days
  return launch > thirtyDaysAgo;
};

export const getProductBadges = (product) => {
  if (!product) return [];

  const BADGE_LIMIT = 1;
  const tags = product?.tags?.map((tag) => tag.toLowerCase());

  const getBadge = {
    bundle: () => {
      const regex = /^(\d+)for(\d+)$/;
      const tag = tags.find((tag) => regex.test(tag));
      if (tag) {
        const [, qty, price] = tag.match(regex);
        return {
          label: `Buy ${qty} for $${price}`,
        };
      }
    },

    'bundle-pp': () => {
      const regex = /^(\d+)for(\d+)pp$/;
      const tag = tags.find((tag) => regex.test(tag));
      if (tag) {
        const [, qty, price] = tag.match(regex);
        return {
          label: `Buy ${qty} for $${price}`,
        };
      }
    },

    'coming-soon': () =>
      tags.includes('coming soon') &&
      ((!product?.availableForSale &&
        (!window.localStorage.getItem('isVIP') ||
          !window.VIP_SHOP_EARLY_STATE?.state ||
          !tags.includes('vip-shop-early'))) ||
        (product?.availableForSale &&
          !window.localStorage.getItem('isVIP') &&
          window.VIP_SHOP_EARLY_STATE?.state &&
          tags.includes('vip-shop-early'))) && {
        label: 'Coming Soon',
      },

    core: () =>
      product?.seasonalCore?.value?.toLowerCase() === 'core' && {
        label: 'Best Seller',
      },

    'gold-foil': () =>
      tags.includes('gold foil') && {
        label: 'Gold Foil',
        shimmer: true,
      },

    new: () =>
      isNew(product) && {
        label: 'New',
      },

    parz: () =>
      tags.includes('parz') && {
        label: '',
      },

    preorder: () =>
      tags.includes('preorder') && {
        label: 'Pre-Order',
      },

    'bundle-save': () =>
      tags.includes('bundle&save') && {
        label: 'BUNDLE & SAVE',
      },

    'silver-foil': () =>
      tags.includes('silver foil') && {
        label: 'Silver Foil',
        shimmer: true,
      },

    'glow-in-dark': () => {
      const isTaggedWithGlow = (tags || []).some((tag) =>
        tag.toLowerCase().startsWith('glow-')
      );
      return (
        isTaggedWithGlow && {
          label: 'GLOWS',
        }
      );
    },

    shimmer: () =>
      tags.includes('badge:shimmer') && {
        label: 'Shimmer',
        shimmer: true,
      },

    bundle3for15: () =>
      tags.includes('bundle3for15') && {
        label: '3 For $15',
      },

    bundle3for40: () =>
      tags.includes('bundle3for40') && {
        label: '3 For $40',
      },

    bundle3for50: () =>
      tags.includes('bundle3for50') && {
        label: '3 For $50',
      },

    bundle3for55: () =>
      tags.includes('bundle3for55') && {
        label: '3 For $55',
      },

    bundle3for95: () =>
      tags.includes('bundle3for95') && {
        label: '3 For $95',
      },

    bundle3for48: () =>
      tags.includes('bundle3for48') && {
        label: '3 For $48',
      },

    bundle3for45: () =>
      tags.includes('bundle3for45') && {
        label: '3 For $45',
      },

    bundle3for60: () =>
      tags.includes('bundle3for60') && {
        label: '3 For $60',
      },

    bundle3for75: () =>
      tags.includes('bundle3for75') && {
        label: '3 For $75',
      },

    doorbuster: () =>
      tags.includes('doorbuster') && {
        label: 'DOORBUSTER',
      },

    'sold-out': () =>
      !product?.availableForSale && {
        label: 'Sold Out',
      },

    'app-exclusive': () =>
      tags.includes('app-exclusive') && {
        label: 'App Exclusive',
      },

    'ship-6/20': () =>
      tags.includes('ship6/20') && {
        label: 'SHIPS 6/20',
      },
  };

  const badgePriority = [
    'bundle3for15',
    'bundle3for40',
    'bundle3for50',
    'bundle3for55',
    'bundle3for95',
    'bundle3for48',
    'bundle3for45',
    'bundle3for60',
    'bundle3for75',
    'bundle-pp',
    'bundle',
    'preorder',
    'glow-in-dark',
    'coming-soon',
    'app-exclusive',
    'sold-out',
    'doorbuster',
    'ship-6/20',
    'core',
    'parz',
    'bundle-save',
    'gold-foil',
    'shimmer',
    'silver-foil',
    'new',
  ];

  const allBadges = badgePriority
    .map((badge) => {
      const badgeData =
        typeof getBadge[badge] === 'function' && getBadge[badge]();
      if (badgeData) {
        badgeData.type = badge;
        return badgeData;
      }
    })
    .filter((badge) => !!badge);

  return allBadges.slice(0, BADGE_LIMIT);
};

export const getProductIntegerId = (gid, type = 'Product') => {
  if (!gid) return null;
  const id = gid?.toString().replace(`gid://shopify/${type}/`, '');
  return parseInt(id);
};

export const getProductStringId = (id, type = 'Product') => {
  if (!id) return null;

  if (id?.toString().includes(`gid://shopify/${type}/`)) {
    return id;
  }

  return `gid://shopify/${type}/${id}`;
};
